import React, {useEffect, useState} from 'react';
import {Modal, Button, Card, Icon} from '@salesforce/design-system-react';
import ChangeCaseInput from "../../../../utils/components/input/changeCaseInput";
import CustomSpinnerOverlay from "../../../../utils/components/spinner/custom_spinner_overlay";
import {SCPDeploymentAPIServiceFactory} from "../../../../services/deployment/scp/factory";
import {GenericDeploymentSubmissionResponse} from "../../../../utils/types/deployments/genericDeploymentResponse";
import {ChangeCaseMetadata} from "../../../../utils/types/gus";
import ImplementationStepSelector from "../../../../utils/components/input/changeCaseImplementationSteps";
import ComplianceChecks from "../../../../components/pcsk_admin/deployment/utils/compliance";

interface ExpandOperationModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedOperationID: string;
    selectedCommitID: string;
    selectedCommitTitle: string;
    selectedCommitDeveloper: string;
    onExpandSuccess: (apiResponse: GenericDeploymentSubmissionResponse) => void;
}

const SCPExpandOperationModal: React.FC<ExpandOperationModalProps> = ({ isOpen, onClose, onExpandSuccess, selectedOperationID, selectedCommitID, selectedCommitTitle, selectedCommitDeveloper }) => {
    const [changeCaseID, setChangeCaseID] = useState<string>('');
    const [isValidChangeCase, setIsValidChangeCase] = useState<boolean>(false);
    const [isExpandInProgress, setIsExpandInProgress] = useState<boolean>(false);
    const [disableExpandButton, setDisableExpandButton] = useState<boolean>(false);
    const [expandAPIResponse, setExpandAPIResponse] = useState<GenericDeploymentSubmissionResponse | undefined>(undefined);
    const [acknowledged, setAcknowledged] = useState<boolean>(false);
    const [ccMetadata, setCCMetadata] = useState<ChangeCaseMetadata | null>(null);
    const [selectedImplStepID, setSelectedImplStepID] = useState<string>('');
    const [triggerComplianceCheck, setTriggerComplianceCheck] = useState<boolean>(false);
    const [lowerEnvDeploymentComplete, setLowerEnvDeploymentComplete] = useState<boolean>(true);

    const scpAPIService = SCPDeploymentAPIServiceFactory.getService();

    useEffect(() => {
        setDisableExpandButton(isExpandInProgress || !isValidChangeCase || !lowerEnvDeploymentComplete || !acknowledged || !isValidImplementationStep())
    }, [isExpandInProgress, isValidChangeCase, lowerEnvDeploymentComplete, acknowledged, selectedImplStepID]);
    useEffect(() => {
        setTriggerComplianceCheck( isValidChangeCase && isValidImplementationStep())
    }, [isValidChangeCase, selectedImplStepID]);
    const disableImplStep = () => {
        return isExpandInProgress || !isValidChangeCase
    }
    const resetImplStep = () => {
        return !isValidChangeCase
    }
    const isValidImplementationStep = () => {
        return selectedImplStepID.length > 0
    }
    const disableComplianceChecks = () => {
        return isExpandInProgress || !isValidChangeCase || !isValidImplementationStep()
    }
    const resetComplianceChecks = () => {
        return !isValidChangeCase ||  !isValidImplementationStep()
    }

    const handleExpand = async () => {
        try {
            setIsExpandInProgress(true);
            const expandResponse = await scpAPIService.expandOperation(changeCaseID, selectedOperationID, selectedImplStepID);
            expandResponse.success && onExpandSuccess(expandResponse)
            setExpandAPIResponse(expandResponse)
        } catch (error) {
            console.log("Error submitting scp operation for Expansion", error);
            setExpandAPIResponse(new GenericDeploymentSubmissionResponse(`${error}`, false))
        } finally {
            setIsExpandInProgress(false);
        }
    };

    const getProcessingContent = () => {
        if (isExpandInProgress) {
            return <>
                <Card hasNoHeader={true}>
                    <div style={{minHeight: "10vh"}}>
                        <div className="slds-m-top_xx-large slds-flex slds-flex_column slds-align-items_center">
                            <CustomSpinnerOverlay size={"medium"}/>
                            <p className="slds-m-top_large slds-align_absolute-center slds-text-color--inverse-weak">
                                Submitting operation for Expansion
                            </p>
                        </div>
                    </div>
                </Card>
            </>
        }

        if (expandAPIResponse) {
            return <>
                <Card hasNoHeader={true}>
                    {expandAPIResponse.success ?
                        <div className="slds-m-around_medium slds-p-around_medium slds-box">
                            <h2 className="slds-text-heading_medium slds-text-align_center slds-m-bottom_medium slds-text-color_success">
                                Expand Request Submitted Successfully!
                            </h2>
                        </div> :
                        <div className="slds-m-around_medium slds-p-around_medium slds-box">
                            <h2 className="slds-text-heading_medium slds-text-align_center slds-m-bottom_medium slds-text-color_destructive">
                                {expandAPIResponse.message}
                            </h2>
                        </div>
                    }
                </Card>
            </>
        }

        return <>
            <Card hasNoHeader={true}>
                <div className="slds-p-around_xx-large slds-m-around--xx-large"></div>
            </Card>
        </>
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            disableClose={isExpandInProgress}
            size="medium"
            dismissOnClickOutside={false}
            footer={
                <>
                    <div
                        className="slds-p-around_medium slds-grid slds-wrap slds-grid_align-spread slds-grid_vertical-align-center">
                        {/* Text and Checkbox - 10 parts */}
                        <div className="slds-col slds-size_10-of-12 slds-grid">
                            <label className="slds-checkbox_toggle slds-grid">
                                <input
                                    type="checkbox"
                                    checked={acknowledged}
                                    disabled={isExpandInProgress || !isValidChangeCase}
                                    onChange={() => {
                                        setAcknowledged(!acknowledged)
                                    }}
                                />
                                <span className="slds-checkbox_faux_container" aria-live="assertive">
                                        <span className="slds-checkbox_faux"></span>
                                        <span className="slds-checkbox_on">Acknowledged</span>
                                        <span className="slds-checkbox_off">Acknowledge? </span>
                                    </span>
                                I acknowledge that the deployment of the previous stagger group has been verified,
                                and evidences are posted on the change case.
                            </label>

                        </div>
                        {/* Button - 2 parts */}
                        <div className="slds-col slds-size_2-of-12">
                            <Button
                                label="Submit for Expansion"
                                onClick={handleExpand}
                                variant="brand"
                                disabled={disableExpandButton}
                            />
                        </div>
                    </div>
                </>
            }
        >
            <Card
                heading={<>
                    <strong className="slds-text-color_success"> Expand Commit: </strong>
                    <strong>{selectedCommitID.length > 0 ? selectedCommitID : "Unknown Commit"}</strong>
                </>}
                icon={<Icon category="standard" name="actions_and_buttons" size="small"/>}
                className="slds-p-vertical_small slds-color__background_gray-3"
                bodyClassName={"slds-p-right_medium slds-p-left_medium"}
            >
                <p> {selectedCommitTitle || "No title provided"}</p>
            </Card>

            <div className="slds-grid slds-wrap">
                {selectedCommitDeveloper.length > 0 && <span className="slds-m-top_x-small slds-m-bottom_small slds-m-horizontal--small slds-badge slds-box_link">
                    Developer: {selectedCommitDeveloper}
                </span>}
                <span className="slds-m-top_x-small slds-m-bottom_small slds-m-horizontal--small slds-badge slds-box_link">
                    Operation ID: {selectedOperationID}
                </span>
            </div>


            <div className="slds-p-around_medium">
                <div className={"slds-box"}>
                    <div className="slds-grid slds-gutters slds-m-around_small">
                        <div className="slds-col slds-size_1-of-5">
                            <label className="slds-form-element__label"><strong>Change Case:</strong></label>
                        </div>
                        <div className="slds-col">
                            <ChangeCaseInput
                                isValidChangeCase={isValidChangeCase}
                                disableInput={isExpandInProgress}
                                changeCaseID={changeCaseID}
                                setChangeCaseID={setChangeCaseID}
                                setIsValidChangeCase={setIsValidChangeCase}
                                deploymentType={"SCPPolicy"}
                                shouldContainCommitID={selectedCommitID}
                                setCCMetadata={setCCMetadata}
                            ></ChangeCaseInput>
                        </div>
                    </div>

                    {/* custom min height maintains the visibility of dropdown options*/}
                    <div className="slds-grid slds-gutters slds-m-horizontal--small slds-m-top--small">
                        <div className="slds-col slds-size_1-of-5">
                            <label className="slds-form-element__label"><strong>Implementation Step:</strong></label>
                        </div>
                        <div className="slds-size_2-of-3">
                            <div className="slds-grid slds-grid_vertical-align-center">
                                <div className="slds-col">
                                    <ImplementationStepSelector
                                        changeCaseMetadata={isValidChangeCase ? ccMetadata : null}
                                        setSelectedStep={setSelectedImplStepID}
                                        disable={disableImplStep()}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/*<div className={"slds-m-vertical--medium"}>*/}
                {/*    <ComplianceChecks*/}
                {/*        triggerComplianceCheck={triggerComplianceCheck}*/}
                {/*        disable={disableComplianceChecks()}*/}
                {/*        reset={resetComplianceChecks()}*/}
                {/*        commitID={selectedCommitID}*/}
                {/*        deploymentType={"SCPPolicy"}*/}
                {/*        artifactName={""}*/}
                {/*        setLowerEnvDeploymentComplete={setLowerEnvDeploymentComplete}*/}
                {/*    />*/}
                {/*</div>*/}
                {getProcessingContent()}
            </div>
        </Modal>
    );
};

export default SCPExpandOperationModal;
